













import { Component, Prop, Ref, Vue, Watch } from "vue-property-decorator";
import { IAssessment } from "../store/main.interfaces";
import vueEcharts from "vue-echarts";
import "zrender/lib/svg/svg";
import "echarts/lib/chart/scatter";
import "echarts/lib/chart/custom";
import "echarts/lib/component/legendScroll";
import "echarts/lib/component/legend";
import "echarts/lib/component/title";
import "echarts/lib/component/tooltip";
import "echarts/lib/component/markArea";
import "echarts/lib/component/markLine";
import "echarts/lib/component/markPoint";
import { NoneFeasabilityResult } from "../store/assessment.calc";
import { EChartOption } from "echarts";
import {
  vocalizeBenefitSuitability,
  vocalizeEaseOfImplementation
} from "../store/assessment.render";
import { isMobile } from "../utils/device.utils";
import Legend = echarts.EChartOption.Legend;
import { COLORS } from "@/colors";
import { mouseEvent } from "@/interfaces/charts.interface";

type chartEntryData = [
  number | NoneFeasabilityResult,
  number,
  number,
  string,
  string,
  IAssessment
];

type hoveredTableItemData = [string, number];

const MOBILE_CONFIG = {
  axisFontSize: 12,
  leftMargin: 5,
  rightMargin: 5,
  topMargin: 5,
  bottomMargin: 30,
  yAxisLabelOffset: 5,
  xAxisLabelOffset: 5,
  legend: {
    type: "scroll",
    bottom: 0,
    left: 5,
    right: 5
  }
};

const DESKTOP_CONFIG = {
  axisFontSize: 12,
  leftMargin: 35,
  rightMargin: 0,
  topMargin: 22,
  bottomMargin: 50,
  yAxisLabelOffset: -17,
  xAxisLabelOffset: -17,
  legend: {
    type: "scroll",
    bottom: 10,
    left: 45
  }
};

function getScreenConfig() {
  return isMobile() ? MOBILE_CONFIG : DESKTOP_CONFIG;
}

Vue.component("v-chart", vueEcharts);
@Component({
  components: { vueEcharts }
})
export default class BubbleChart extends Vue {
  @Prop() assessmentEntries!: IAssessment[];
  @Prop() hoveredTableItem!: IAssessment;

  @Ref("chart") readonly chart!: any;

  @Watch("hoveredTableItem")
  onHoveredTableItemChange(val: IAssessment | null) {
    this.chart.dispatchAction({
      type: "downplay"
    });

    if (val) {
      const itemData: hoveredTableItemData = this.getHoveredTableItemData(val);
      this.chart.dispatchAction({
        type: "highlight",
        seriesName: itemData[0],
        dataIndex: itemData[1]
      });
    }
  }

  getHoveredTableItemData(item: IAssessment): hoveredTableItemData {
    const seriesName: string = item.department || this.$t("generics.terms.noDepartment") as string;
    const departmentEntries: IAssessment[] = this.assessmentEntries.filter(
      entry => entry.department == item.department
    );
    return [seriesName, departmentEntries.indexOf(item)];
  }

  createChartData(department: string): chartEntryData[] {
    return this.assessmentEntries
      .filter(entry => (entry.department || this.$t("generics.terms.noDepartment") as string) === department)
      .map(entry => {
        return [
          entry.outputs.easeOfImplementation,
          entry.outputs.benefitSuitability,
          entry.outputs.bandwidthFreed,
          entry.name,
          entry.department,
          entry
        ] as chartEntryData;
      });
  }

  getDepartments(): string[] {
    const departments = new Set<string>();
    let hasNoDepartment: boolean = false;
    this.assessmentEntries.forEach(entry => {
      if (entry.department) {
        departments.add(entry.department);
      } else {
        hasNoDepartment = true;
      }
    });

    return hasNoDepartment ? [this.$t("generics.terms.noDepartment") as string, ...Array.from(departments).sort()] : [...Array.from(departments).sort()];
  }

  generateSeriesByDepartment(): EChartOption.Series[] {
    const allDepartments = this.getDepartments();
    const series: EChartOption.Series[] = [];
    allDepartments.forEach(department => {
      series.push({
        type: "scatter",
        name: department,
        data: this.createChartData(department),
        symbolSize: (entryData: chartEntryData) => {
          return Math.min(Math.max(3, Math.sqrt(entryData[2]) * 1.2), 90);
        }
      });
    });

    return series;
  }

  get chartOptionsBar(): EChartOption {
    const generatedSeries = this.generateSeriesByDepartment();
    return {
      title: {
        show: false
      },
      legend: {
        ...(getScreenConfig().legend as Legend),
        data: this.getDepartments(),
        textStyle: {
          color: "#000",
          fontSize: 12
        }
      },
      color: COLORS,
      grid: {
        top: getScreenConfig().topMargin,
        right: getScreenConfig().rightMargin,
        left: getScreenConfig().leftMargin,
        bottom: getScreenConfig().bottomMargin,
        borderColor: "transparent",
        show: true
      },
      xAxis: {
        name: this.$t("generics.terms.easeOfImplementation") as string,
        nameLocation: "middle",
        nameGap: 25,
        type: "value",
        show: false,
        inverse: true,
        max: 100,
        min: 0
      },
      yAxis: {
        name: this.$t("generics.terms.benefit2") as string,
        type: "value",
        min: 0,
        max: 100,
        show: false
      },
      series: [
        ...generatedSeries,
        {
          type: "scatter",
          markPoint: {
            animation: false,
            silent: true,
            symbol: [],
            itemStyle: {
              color: "transparent"
            },
            symbolSize: 1,
            label: {
              show: true,
              formatter: (a: any) => {
                return a.name;
              },
              textBorderWidth: 0,
              fontSize: getScreenConfig().axisFontSize,
              fontWeight: "bold",
              fontFamily: "Roboto",
              color: "grey",
              align: "center",
              position: [getScreenConfig().yAxisLabelOffset, 0],
              rotate: 90
            },
            data: [
              {
                name: this.$t("components.bubbleChart.highBenefit"),
                coord: [100, 75]
              },
              {
                name: this.$t("components.bubbleChart.lowSuitability"),
                coord: [100, 25]
              }
            ]
          }
        },
        {
          type: "scatter",
          markPoint: {
            animation: false,
            silent: true,
            symbol: [],
            itemStyle: {
              color: "transparent"
            },
            symbolSize: 1,
            label: {
              show: true,
              formatter: (a: any) => {
                return a.name;
              },
              textBorderWidth: 0,
              fontSize: getScreenConfig().axisFontSize,
              fontWeight: "bold",
              fontFamily: "Roboto",
              color: "grey",
              align: "center",
              position: [0, getScreenConfig().xAxisLabelOffset]
            },
            data: [
              {
                name: this.$t("components.bubbleChart.lowComplexity"),
                coord: [25, 100]
              },
              {
                name: this.$t("components.bubbleChart.highComplexity"),
                coord: [75, 100]
              }
            ]
          }
        },
        {
          type: "custom",
          silent: true,
          renderItem: (params: any, api: any) => {
            if (params.context.rendered) {
              // return;
            }

            params.context.rendered = true;
            const type = ["rect", "line"][api.value(0)];
            const x = api.value(1);
            const y = api.value(2);
            const x2 = api.value(3);
            const y2 = api.value(4);
            const startCoord = api.coord([x, y]);
            const endCoord = api.coord([x2, y2]);

            return {
              type,
              shape: {
                // rect
                x: startCoord[0],
                y: startCoord[1],
                width: endCoord[0] - startCoord[0],
                height: endCoord[1] - startCoord[1],
                r: (endCoord[0] - startCoord[0]) * 0.05,

                // line
                x1: startCoord[0],
                x2: endCoord[0],
                y1: startCoord[1],
                y2: endCoord[1]
              },
              style: api.style({
                fill: "#F8F8F8",
                stroke: type === "line" ? "#E0E0E0" : "transparent"
              })
            };
          },
          data: [
            [0, 100, 100, 0, 0],
            [1, 50, 0, 50, 100],
            [1, 0, 50, 100, 50]
          ]
        } as any
      ],
      tooltip: {
        trigger: "item",
        axisPointer: {
          type: "none"
        },
        formatter: (chartData: any) => {
          const entryData: chartEntryData = chartData.value;
          return this.$t("components.bubbleChart.formattedBubbleChartData", {
            entryName: entryData[3],
            easeOfImplementation: vocalizeEaseOfImplementation(entryData[5]),
            benefitSuitability: vocalizeBenefitSuitability(entryData[5]),
            freedCapacity: entryData[2].toFixed(1)
          }) as string;
        }
      }
    };
  }

  mouseover(event: any) {
    const id = event.data[5].id;
    this.$emit("mouseover-series", id);
  }

  mouseout(event: any) {
    const id = event.data[5].id;
    this.$emit("mouseout-series", id);
  }
}
